import React, { useState } from "react";
import { Dropdown, Button, message, Space, Typography } from "antd";
import type { MenuProps } from "antd";
import { WalletOutlined, DownOutlined, DisconnectOutlined, CheckCircleFilled } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useMultiWallet } from "../hooks/useMultiWallet";
import { NETWORKS, NETWORK_ORDER, SupportedNetwork, truncateAddress } from "../constants/networks";
import "./WalletHeaderButton.css";

const { Text } = Typography;

/**
 * Кнопка кошелька в шапке для браузера и PWA. Заменяет TonConnectButton,
 * который подключал только TON: показывает активную сеть и адрес, а по нажатию
 * открывает список сетей — тот же, что в переключателе на экране баланса.
 * В мини-аппе не используется: там остаётся родной TonConnectButton.
 */
const WalletHeaderButton: React.FC = () => {
  const { t } = useTranslation();
  const wallet = useMultiWallet();
  const [open, setOpen] = useState(false);

  const describeError = (code: string): string => {
    switch (code) {
      case "connector_unavailable":
        return t("Wallet connector is unavailable. Reload the page or try another browser.");
      case "tron_install_extension":
        return t("Install the TronLink extension to connect a TRON wallet.");
      case "tron_open_in_app":
        return t("Open this page inside the TronLink app to connect a TRON wallet.");
      default:
        return code;
    }
  };

  const handleConnect = async (network: SupportedNetwork) => {
    setOpen(false);
    try {
      await wallet.connect(network);
    } catch (e: any) {
      const code = e?.message || "";
      if (/reject|cancel|user/i.test(code)) return;
      message.error(describeError(code));
    }
  };

  const handleDisconnect = async (network: SupportedNetwork) => {
    setOpen(false);
    try {
      await wallet.disconnect(network);
    } catch (e) {
      console.warn(e);
    }
  };

  const items: MenuProps["items"] = [];
  NETWORK_ORDER.forEach((net) => {
    const meta = NETWORKS[net];
    const address = wallet.addresses[net] || wallet.linkedAddresses[net];
    const isActive = wallet.activeNetwork === net;
    items.push({
      key: net,
      label: (
        <div
          className="whb-item"
          onClick={() => {
            wallet.setActiveNetwork(net);
            setOpen(false);
            if (!address) handleConnect(net);
          }}
        >
          <img src={meta.icon} alt={meta.name} className="whb-item-icon" />
          <div className="whb-item-text">
            <Text strong style={{ fontSize: 13 }}>
              {meta.name}
              {isActive && <CheckCircleFilled style={{ color: "#52c41a", marginLeft: 6, fontSize: 11 }} />}
            </Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {address ? truncateAddress(address) : t("Connect")}
            </Text>
          </div>
          {address && (
            <Button
              type="text"
              size="small"
              danger
              icon={<DisconnectOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleDisconnect(net);
              }}
              title={t("Disconnect") as string}
            />
          )}
        </div>
      ),
    });
  });

  const meta = NETWORKS[wallet.activeNetwork];
  const currentAddr = wallet.currentAddress || wallet.linkedAddresses[wallet.activeNetwork];
  const connected = wallet.isConnected || Boolean(currentAddr);

  return (
    <Dropdown
      menu={{ items }}
      trigger={["click"]}
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      overlayClassName="whb-dropdown"
    >
      <button type="button" className={`whb-button ${connected ? "whb-connected" : ""}`} style={connected ? { borderColor: meta.color } : undefined}>
        <Space size={5}>
          {connected ? (
            <img src={meta.icon} alt={meta.name} className="whb-icon" />
          ) : (
            <WalletOutlined style={{ fontSize: 14 }} />
          )}
          <span className="whb-label">
            {connected ? truncateAddress(currentAddr || "", 4, 4) : t("Connect")}
          </span>
          <DownOutlined style={{ fontSize: 9, opacity: 0.7 }} />
        </Space>
      </button>
    </Dropdown>
  );
};

export default WalletHeaderButton;
